import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { SERVER_URL } from "../../constants";
import { useAuth, useUser } from "@clerk/clerk-react";

const CartPage = () => {
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const [cart, setCart] = useState([]);
  const [amount, setAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCart = async () => {
      if (isSignedIn) {
        try {
          const response = await axios.get(`${SERVER_URL}/api/v1/cart/${user.id}`);
          setCart(response.data.cart || []);
          calculateTotal(response.data.cart);
        } catch (err) {
          console.error("Error fetching cart:", err);
          setError("Failed to load cart.");
        } finally {
          setLoading(false);
        }
      }
    };

    fetchCart();
  }, [isSignedIn, user?.id]);

  const calculateTotal = (updatedCart) => {
    const total = updatedCart.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );
    setAmount(total);
  };

  const increaseQuantity = (productId) => {
    const updatedCart = cart.map((item) =>
      item.productId === productId
        ? { ...item, quantity: item.quantity + 1 }
        : item
    );
    setCart(updatedCart);
    calculateTotal(updatedCart);
  };

  const decreaseQuantity = (productId) => {
    const updatedCart = cart.map((item) =>
      item.productId === productId && item.quantity > 1
        ? { ...item, quantity: item.quantity - 1 }
        : item
    );
    setCart(updatedCart);
    calculateTotal(updatedCart);
  };

  const handleRemove = async (productId) => {
    try {
      await axios.delete(`${SERVER_URL}/api/v1/cart/remove/${productId}/${user.id}`);
      const updatedCart = cart.filter((item) => item.productId !== productId);
      setCart(updatedCart);
      calculateTotal(updatedCart);
    } catch (err) {
      console.error("Error removing item:", err);
    }
  };

  const proceedToCheckout = async () => {
  
      navigate("/checkout");
    
  };

  if (!isSignedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-xl font-medium text-gray-600">
          Please sign in to view your cart.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl font-medium text-gray-600">Loading cart...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-xl font-medium text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen  pt-6 sm:pt-8 md:pt-12 lg:pt-30 mt-[-50px]">
      <div className="max-w-7xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold text-gray-900 text-center mb-12">
          Your Cart
        </h1>
        {cart.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-xl text-gray-500">Your cart is empty.</p>
          </div>
        ) : (
          <div>
            <div className="grid gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {cart.map((item, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden transform transition duration-300 hover:scale-105"
                >
                  <img
                    src={item.image}
                    alt={item.name}
                    className="object-cover w-full h-48"
                  />
                  <div className="p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">
                      {item.name}
                    </h2>
                    <p className="text-sm text-gray-600">Size: {item.variation?.size || "N/A"}</p>
                    <p className="text-sm text-gray-600">Color: {item.variation?.color || "N/A"}</p>
                    <p className="text-lg font-medium text-gray-900 mt-2">
                      Rs. {item.price}
                    </p>
                    <div className="flex items-center justify-between mt-4">
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={() => decreaseQuantity(item.productId)}
                          className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                        >
                          -
                        </button>
                        <span className="text-lg font-medium">{item.quantity}</span>
                        <button
                          onClick={() => increaseQuantity(item.productId)}
                          className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
                        >
                          +
                        </button>
                      </div>
                      <button
                        onClick={() => handleRemove(item.productId)}
                        className="text-red-600 hover:text-red-700 transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-12 bg-white rounded-2xl shadow-lg p-8">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-semibold text-gray-900">
                  Total Amount
                </h3>
                <p className="text-3xl font-bold text-gray-900">
                  Rs. {amount.toFixed(2)}
                </p>
              </div>
              <div className="mt-8 text-center">
                <button
                  onClick={proceedToCheckout}
                  className="inline-flex items-center px-8 py-4 text-lg font-semibold text-white bg-indigo-600 rounded-full hover:bg-indigo-700 transition-colors duration-300"
                >
                  Proceed to Checkout
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPage;
